import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import CloseButton from "react-bootstrap/CloseButton";
import "./style.scss";
import CommonButton from "../../commonButton";
import DateIcon from "../../../assets/images/custom_documentation/Date.svg";
import { postData } from "../../../api";
import { toast } from "react-toastify";

const CreateTaskModal = ({ show, onHide, documentData, onTaskSave }) => {
  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignToUsers, setAssignToUsers] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleSave = async () => {
    // Validate required fields
    const newErrors = {};

    if (!taskName.trim()) {
      newErrors.taskName = "Task name is required";
    }

    if (!dueDate) {
      newErrors.dueDate = "Due date is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clear any existing errors
    setErrors({});

    try {
      const payload = {
        name: taskName,
        description: description,
        due_date: dueDate,
        client_document_id: documentData?.id,
        assign_task: assignToUsers ? 1 : 0,
        user_id:
          assignToUsers && selectedUsers.length > 0
            ? selectedUsers.join(",")
            : "",
      };

      const response = await postData("task-form-update", {}, payload);

      if (response.status) {
        // Reset form on success
        setTaskName("");
        setDescription("");
        setDueDate("");
        setAssignToUsers(false);
        setUsersList([]);
        setSelectedUsers([]);
        setShowUserDropdown(false);
        setDropdownOpen(false);
        toast.success(response.message, {
          theme: "colored",
          autoClose: 3000,
        });

        // Call onTaskSave callback if provided
        if (typeof onTaskSave === "function") {
          onTaskSave();
        }

        if (typeof onHide === "function") {
          onHide();
        }
      } else {
        console.error("Task creation failed:", response.message);
      }
    } catch (error) {
      console.error("Error creating task:", error);
    }
  };

  const handleCancel = () => {
    // Reset form
    setTaskName("");
    setDescription("");
    setDueDate("");
    setAssignToUsers(false);
    setUsersList([]);
    setSelectedUsers([]);
    setShowUserDropdown(false);
    setDropdownOpen(false);
    setErrors({});

    if (typeof onHide === "function") {
      onHide();
    }
  };

  const handleCheckboxChange = async (e) => {
    const isChecked = e.target.checked;
    setAssignToUsers(isChecked);
    const payload = {
      client_document_id: documentData?.id,
    };
    if (isChecked) {
      setShowUserDropdown(true);
      try {
        const response = await postData("task-form-fetch", {}, payload);
        if (response.status && response.data && response.data.users_lists) {
          setUsersList(response.data.users_lists);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    } else {
      setShowUserDropdown(false);
      setDropdownOpen(false);
      setSelectedUsers([]);
      setUsersList([]);
    }
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSelectDeselectAll = () => {
    if (selectedUsers.length === usersList.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usersList.map((user) => user.id));
    }
  };

  return (
    <Modal show={show} centered onHide={onHide} className="common-popup">
      <Modal.Header className="create-task-modal-header">
        <div className="form-modal">
          <Modal.Title>Create Task</Modal.Title>
        </div>
        <CloseButton onClick={onHide} className="close-cta-wrapper" />
      </Modal.Header>
      <Modal.Body>
        <div className="create-task-form">
          <div className="form-group">
            <label htmlFor="taskName">Task Name</label>
            <div className="form-control-wrapper">
              <input
                type="text"
                id="taskName"
                className={`form-control ${errors.taskName ? "is-invalid" : ""}`}
                placeholder="Enter task name"
                value={taskName}
                onChange={(e) => {
                  setTaskName(e.target.value);
                  if (errors.taskName) {
                    setErrors((prev) => ({ ...prev, taskName: "" }));
                  }
                }}
              />
              {errors.taskName && (
                <div className="error-message">{errors.taskName}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <div className="form-control-wrapper">
              <textarea
                id="description"
                className="form-control"
                placeholder="Enter task description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                style={{ resize: "vertical" }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="dueDate">Due Date</label>
            <div className="form-control-wrapper">
              <div className="date-input-wrapper">
                <input
                  type="date"
                  id="dueDate"
                  className={`form-control ${errors.dueDate ? "is-invalid" : ""}`}
                  value={dueDate}
                  onChange={(e) => {
                    setDueDate(e.target.value);
                    if (errors.dueDate) {
                      setErrors((prev) => ({ ...prev, dueDate: "" }));
                    }
                  }}
                />
                <button
                  type="button"
                  className="calendar-button"
                  onClick={() =>
                    document.getElementById("dueDate").showPicker?.()
                  }
                >
                  <img src={DateIcon} alt="calendar-icon" />
                </button>
              </div>
              {errors.dueDate && (
                <div className="error-message">{errors.dueDate}</div>
              )}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="assignToUsers"
              name="assignToUsers"
              checked={assignToUsers}
              onChange={handleCheckboxChange}
            />
            <label className="checkbox-label" htmlFor="assignToUsers">
              Assign task to user(s)
            </label>
          </div>

          {showUserDropdown && (
            <div className="form-group user-selection-dropdown">
              <label>Select user(s)</label>
              <div className="dropdown-wrapper" ref={dropdownRef}>
                <div
                  className="dropdown-header"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span>
                    {selectedUsers.length > 0
                      ? `${selectedUsers.length} user(s) selected`
                      : "Select user(s)"}
                  </span>
                  <span className="dropdown-arrow">
                    {dropdownOpen ? "▲" : "▼"}
                  </span>
                </div>

                {dropdownOpen && (
                  <div className="dropdown-content">
                    <div
                      className="select-all-option"
                      onClick={handleSelectDeselectAll}
                    >
                      {selectedUsers.length === usersList.length
                        ? "De-select All"
                        : "Select All"}
                    </div>

                    {usersList.map((user) => (
                      <div key={user.id} className="user-option">
                        <input
                          type="checkbox"
                          id={`user-${user.id}`}
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelection(user.id)}
                        />
                        <label htmlFor={`user-${user.id}`}>{user.name}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="modal-buttons">
            <CommonButton
              onClick={handleSave}
              variant="primary"
              btnText="SAVE"
            />
            <CommonButton
              onClick={handleCancel}
              variant="secondary"
              btnText="CANCEL"
            />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateTaskModal;
